import { IconButton } from '@mui/material';
import React, { FC } from 'react';
import styled from 'styled-components';
import {
	AcceptIcon,
	CrossRed,
	PartialAcceptedIcon,
	RejectIcon,
	TickGreen,
} from '../../../components/Icons';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import { RESTAURANT } from '@src/Constants';
import StyledStatus from './StyledStatus';
import { setPreviewImages } from '@src/store/reducers/UIReducer';

const StyledProductCard = styled.div`
	width: 95%;
	height: auto;
	display: flex;
	background: white;
	border-radius: 10px;
	border: 1.5px solid #f1f1f1;
	padding: 10px;
	gap: 14px;
`;

const QuantityContainer = styled.div`
	min-width: 40px;
	min-height: 40px;
	max-height: 40px;
	min-width: 40px;
	background: #fff9e6;
	border-radius: 8px;
	border: 1.5px solid #e9e9e9;
	margin-top: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
`;
const ProductSize = styled.div`
	color: #acacac;
	font-size: 12px;
	font-weight: 600;
`;

const ProductInfoContainer = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 4px;
	#bottomContainer {
		display: flex;
		flex-direction: column;
		#productInfo {
			display: flex;
			justify-content: space-between;

			#priceContainer {
				display: flex;
				align-items: center;
				gap: 12px;
			}
		}
		#image {
			color: #5c5c5c;
			font-size: 12px;
			font-weight: 600;
		}

		#status-container {
			display: flex;
			align-items: center;
			gap: 12px;

			span {
				color: var(--default-text-color);
				font-size: 12px;
				font-style: italic;
				font-weight: 600;
				line-height: 20px;
			}
			@media screen and (max-width: 800px) {
				align-items: flex-start;
				flex-direction: column;
				gap: 0px;
			}
		}
	}
`;

const ProductName = styled.div`
	margin-top: 10px;
	color: black;
	font-size: 14px;
	font-weight: 600;
	max-width: 204px;
	overflow: hidden;
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2;
	text-overflow: ellipsis;
	word-wrap: break-word;
`;

const ProductPrice = styled.div`
	color: var(--default-text-color);
	font-size: 12px;
	font-weight: 700;
	word-wrap: break-word;
`;

const ProductIssue = styled.div`
	font-weight: 600;
	color: #f26184;
	font-size: 14px;
`;

const QuantityText = styled.div`
	color: var(--default-text-color);
	font-size: 16px;
	font-family: 'Nunito Sans';
	font-weight: 600;
	word-wrap: break-word;
`;

const ButtonsContainer = styled.div`
	display: flex;
	align-items: flex-end;
	gap: 10px;

	.icon-container {
		display: flex;
		align-items: center;
		justify-content: center;
		background: #f7f5f4;
		width: 36px;
		height: 36px;
		border-radius: 50%;
		transition: background 0.3s ease;

		&:hover {
			background: #d1d1d1;
		}

		&:active {
			background: #a1a1a1;
		}
	}
`;

const CommentContainer = styled.div`
	color: var(--default-text-color);
	font-size: 12px;
	font-style: italic;
	font-weight: 600;
	line-height: 17.6px;
	width: 214px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

interface Props {
	product: any;
}

const ProductCard: FC<Props> = ({ product }) => {
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const isSupplier = currentUser?.type?.toLowerCase() !== RESTAURANT;
	const isAllowed = ['pending', 'accepted'].includes(product?.delivery_acc_status?.toLowerCase());

	const dispatch = useAppDispatch();

	const openPreview = () => {
		dispatch(setPreviewImages([product?.delivery_acc_image]));
	};

	const buttonsContainer = () => {
		const status = product?.delivery_acc_status?.toLowerCase();
		if (status === 'accepted') {
			return (
				<ButtonsContainer id="button-conatiner">
					<div id="button-1" className="icon-container">
						<TickGreen id="button-accept" />
					</div>
				</ButtonsContainer>
			);
		} else if (status === 'rejected') {
			return (
				<ButtonsContainer id="button-conatiner">
					<div id="button-1" className="icon-container">
						<CrossRed id="button-accept" />
					</div>
				</ButtonsContainer>
			);
		} else if (status === 'partially_accepted') {
			return (
				<ButtonsContainer id="button-conatiner">
					<div id="button-1" className="icon-container">
						<PartialAcceptedIcon id="button-accept" />
					</div>
				</ButtonsContainer>
			);
		} else if (status === 'pending' && isSupplier) {
			return <StyledStatus status="pending" style={{ fontSize: '14px' }} />;
		}
		return (
			<ButtonsContainer id="button-conatiner">
				<div
					id="button-1"
					className="icon-container"
					onClick={(e) => product?.onClick(e, 'accept')}
				>
					<AcceptIcon id="button-accept" />
				</div>
				<div
					id="button-2"
					className="icon-container"
					onClick={(e) => product?.onClick(e, 'reject')}
				>
					<RejectIcon id="button-reject" />
				</div>
			</ButtonsContainer>
		);
	};

	return (
		<StyledProductCard onClick={(e) => product?.openProductDetails(e)}>
			<QuantityContainer>
				<QuantityText>{product?.quantity}</QuantityText>
			</QuantityContainer>
			<ProductInfoContainer>
				<ProductName>{product?.name}</ProductName>
				<div id="bottomContainer">
					<div style={{ display: 'flex', justifyContent: 'space-between' }}>
						<div id="productInfo">
							<div id="priceContainer">
								<ProductSize>{product?.unit}</ProductSize>
								<ProductPrice>₹ {product?.price}</ProductPrice>
							</div>
						</div>
						{buttonsContainer()}
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
						<div id="status-container">
							<StyledStatus status={product?.delivery_acc_status?.toLowerCase()} />
							{!isAllowed ? (
								product?.delivery_acc_reason?.toLowerCase() === 'other' ? (
									<span>other reason</span>
								) : (
									<span>
										{product?.delivery_acc_quantity} {product?.delivery_acc_reason}
									</span>
								)
							) : null}
						</div>
						{product?.delivery_acc_comments ? (
							<CommentContainer>“{product?.delivery_acc_comments}”</CommentContainer>
						) : null}
						{!isAllowed && product?.delivery_acc_image ? (
							<p id="image" onClick={openPreview}>
								1 picture attached
							</p>
						) : null}
					</div>
				</div>
			</ProductInfoContainer>
		</StyledProductCard>
	);
};

export default ProductCard;
